import {createSection} from 'common/src/modules/shared/copilot/utils/formatters/createSection';
import formatSections from 'common/src/modules/shared/copilot/utils/formatters/formatSections';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import isEqual from 'lodash/isEqual';
import {BrandGuidelines} from '../../brandGuidelines.types';

export type BrandGuidelinesContext = {
  localBrandGuidelines: BrandGuidelines | null;
  submitRequired: boolean;
};

function brandGuidelinesContext(store: CLStore): AgentContext<BrandGuidelinesContext> {
  function getter(): BrandGuidelinesContext {
    const state = store.getState().brandGuidelinesPage.brandGuidelines;

    return {
      localBrandGuidelines: state.localData,
      submitRequired: state.UnsavedDataInUIForm,
    };
  }

  function formatter({
    prevContext,
    currentContext,
  }: {
    prevContext: BrandGuidelinesContext | null;
    currentContext: BrandGuidelinesContext;
  }): string | null {
    const {localBrandGuidelines, submitRequired} = currentContext;

    const sections = [
      createSection<BrandGuidelines>('Brand Identity', 'brandIdentity'),
      createSection<BrandGuidelines>('Brand Voice Guidelines', 'brandVoiceGuidelines'),
      createSection<BrandGuidelines>('Image Guidelines', 'imageGuidelines'),
      createSection<BrandGuidelines>('Brand Colors', 'colors'),
    ];

    const submitStatus = submitRequired
      ? ' (Unsaved changes - submit required)'
      : ' (All changes saved)';

    if (!prevContext || !prevContext.localBrandGuidelines) {
      // initial context
      return !!localBrandGuidelines
        ? formatSections(sections, localBrandGuidelines, false) + submitStatus
        : 'Brand Guidelines not loaded.';
    }

    const prevBrandGuidelines = prevContext.localBrandGuidelines;

    if (!localBrandGuidelines) {
      return 'Brand guidelines have been cleared.';
    }

    // format only the changed sections
    const changedSections = sections.filter(
      (section) =>
        !isEqual(prevBrandGuidelines[section.attrName], localBrandGuidelines[section.attrName]),
    );

    const changesText =
      changedSections.length === 0
        ? 'Brand guidelines have been updated (unspecified changes).'
        : formatSections(changedSections, localBrandGuidelines, true);

    return changesText + submitStatus;
  }

  return {
    title: 'Brand Guidelines',
    getter,
    formatter,
  };
}

export default brandGuidelinesContext;
